import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, ChevronsUpDown, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useDebounce } from '@/hooks/useDebounce';
import { useLocationAccount, useLocationAccountsInfinite } from '../hooks';
import type { Account } from '../types';

interface LocationAccountComboboxProps {
  value?: string;
  onChange: (accountId: string) => void;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
}

const ALL_VALUE = 'all';

function getAccountLabel(account: Account): string {
  return account.accountNumber
    ? `${account.name} (${account.accountNumber})`
    : account.name;
}

export function LocationAccountCombobox({
  value,
  onChange,
  disabled = false,
  className,
  placeholder = 'All Accounts',
}: LocationAccountComboboxProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);

  const infiniteQuery = useLocationAccountsInfinite(debouncedSearch);
  const { fetchNextPage, hasNextPage, isFetchingNextPage } = infiniteQuery;
  const loadedAccounts = useMemo(
    () => infiniteQuery.data?.pages.flatMap((page) => page.accounts) ?? [],
    [infiniteQuery.data],
  );
  const total = infiniteQuery.data?.pages[0]?.total ?? 0;

  const isExplicitValue = !!value && value !== ALL_VALUE;
  const selectedFromLoaded = isExplicitValue
    ? loadedAccounts.find((account) => account.id === value)
    : undefined;
  const fallbackLookupId = isExplicitValue && !selectedFromLoaded ? value : undefined;
  const fallbackQuery = useLocationAccount(fallbackLookupId);
  const selected = selectedFromLoaded ?? fallbackQuery.data ?? null;

  useEffect(() => {
    if (!open) setSearch('');
  }, [open]);

  const sentinelRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const node = sentinelRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0]?.isIntersecting &&
          hasNextPage &&
          !isFetchingNextPage
        ) {
          void fetchNextPage();
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [
    open,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  ]);

  const triggerLabel = selected ? getAccountLabel(selected) : placeholder;
  const showingLabel = total > 0
    ? `Showing ${loadedAccounts.length} of ${total}`
    : null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            'h-7 w-full justify-between text-xs font-normal',
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronsUpDown className="ml-1 h-3 w-3 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[260px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search accounts..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>
              {infiniteQuery.isError ? (
                <button
                  type="button"
                  className="w-full px-2 py-2 text-left text-xs"
                  onClick={() => {
                    void infiniteQuery.refetch();
                  }}
                >
                  Failed to load. Click to retry.
                </button>
              ) : infiniteQuery.isLoading ? (
                'Loading...'
              ) : (
                'No accounts found.'
              )}
            </CommandEmpty>
            <CommandGroup>
              <CommandItem
                value="__all__"
                onSelect={() => {
                  onChange(ALL_VALUE);
                  setOpen(false);
                }}
              >
                <Check
                  className={cn(
                    'mr-2 h-4 w-4',
                    !value || value === ALL_VALUE ? 'opacity-100' : 'opacity-0',
                  )}
                />
                {placeholder}
              </CommandItem>
              {loadedAccounts.map((account) => (
                <CommandItem
                  key={account.id}
                  value={account.id}
                  onSelect={() => {
                    onChange(account.id);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'mr-2 h-4 w-4',
                      value === account.id ? 'opacity-100' : 'opacity-0',
                    )}
                  />
                  <span className="truncate">{account.name}</span>
                  {account.accountNumber && (
                    <span className="ml-2 shrink-0 text-muted-foreground">
                      {account.accountNumber}
                    </span>
                  )}
                </CommandItem>
              ))}
              {hasNextPage && (
                <div
                  ref={sentinelRef}
                  className="flex items-center justify-center py-2 text-xs text-muted-foreground"
                >
                  {isFetchingNextPage ? (
                    <>
                      <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                      Loading more...
                    </>
                  ) : (
                    <span>&nbsp;</span>
                  )}
                </div>
              )}
            </CommandGroup>
          </CommandList>
          {showingLabel && (
            <div className="border-t px-2 py-1 text-xs text-muted-foreground">
              {showingLabel}
            </div>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
